import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';
import {
  Card,
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableColumn,
} from '@/components/ui';
import { Input } from '@/components/forms';
import { Paginator } from '@/components/ui/Paginator/Paginator';
import { useModalStore } from '@/stores';
import { DeleteConfirmationModal } from '@/components/modals/DeleteConfirmationModal';
import { InfoModal } from '@/components/modals/InfoModal';
import styles from './Providers.module.css';

const PAGE_SIZE = 10;

interface Service {
  id: string;
  name: string;
  slug: string;
  description: string;
  status: string;
  category_names: string[];
}

export function Services() {
  const { openModal, closeModal } = useModalStore();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data: allData, isLoading } = useQuery<Service[]>({
    queryKey: ['services'],
    queryFn: async () => {
      const response = await api.get(API_ROUTES.services);
      return Array.isArray(response.data) ? response.data : (response.data.results ?? []);
    },
  });

  const filteredData = useMemo(() => {
    if (!allData) return [];
    if (!search.trim()) return allData;
    const s = search.toLowerCase();
    return allData.filter((svc) => svc.name.toLowerCase().includes(s));
  }, [allData, search]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / PAGE_SIZE));
  const paginatedData = filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to page 1 when search changes
  const handleSearch = (val: string) => {
    setSearch(val);
    setPage(1);
  };

  const deleteMutation = useMutation({
    mutationFn: async (slug: string) => {
      await api.delete(`${API_ROUTES.services}${slug}/`);
    },
    onSuccess: () => {
      toast.success('Service deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['services'] });
      closeModal();
    },
    onError: (error: unknown) => {
      const apiError = error as { response?: { data?: { error?: string } } };
      toast.error(apiError.response?.data?.error || 'Error deleting service');
    },
  });

  const handleDelete = (service: Service) => {
    openModal(
      <DeleteConfirmationModal
        title="Delete Service"
        itemName={service.name}
        onConfirm={() => deleteMutation.mutate(service.slug)}
        isDeleting={deleteMutation.isPending}
      />,
    );
  };

  return (
    <div className={styles.container}>
      <Card className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <Button
            variant="primary"
            onClick={() =>
              openModal(
                <InfoModal
                  title="Coming Soon"
                  message="The functionality to add services will be available soon."
                />,
              )
            }
          >
            <FontAwesomeIcon icon={faPlus} /> Add Service
          </Button>
        </div>

        <div className={styles.filters}>
          <Input
            name="search"
            label="Search"
            placeholder="Search services by name..."
            value={search}
            setValue={handleSearch}
            variant="bordered"
            isClearable
          />
        </div>

        <Table aria-label="Services list" radius={8}>
          <TableHeader>
            <TableColumn>Name</TableColumn>
            <TableColumn>Categories</TableColumn>
            <TableColumn>Status</TableColumn>
            <TableColumn>Actions</TableColumn>
          </TableHeader>
          <TableBody isLoading={isLoading}>
            {paginatedData.map((service) => (
              <TableRow key={service.id}>
                <TableCell>{service.name}</TableCell>
                <TableCell>{service.category_names?.join(', ') || '-'}</TableCell>
                <TableCell>
                  <span
                    className={`${styles.statusBadge} ${service.status === 'active' ? styles.statusActive : styles.statusInactive}`}
                  >
                    {service.status}
                  </span>
                </TableCell>
                <TableCell>
                  <Button variant="danger" size="sm" onClick={() => handleDelete(service)}>
                    <FontAwesomeIcon icon={faTrash} /> Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <Paginator page={page} numPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </Card>
    </div>
  );
}
